// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { Link } from '@/components/link';
import {
  Close,
  OpenInNew,
  SearchOutlined,
} from '@/constants/hardfin-standard-icons';
import { MemberOnDay } from '@/membership/utilities/membership-days';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// A serial line over a location line, plus the gap below the row. The window
// needs one number, so the row cannot grow with its content.
const ROW_HEIGHT = 52;

// Until the column has been measured, so the first paint is not an empty list.
const FALLBACK_LIST_HEIGHT = 240;

interface Props {
  members: MemberOnDay[];
  // Shown when the day has no members at all, which reads differently from a
  // search that matched nothing.
  emptyMessage: string;
  // Set where the list stands beside the chart rather than inside a dialog of
  // its own, so it can be dismissed to give the chart the room back.
  onClose?: () => void;
}

/**
 * The members on site on the scrubbed day. The SKU chips above the report
 * decide which SKUs reach it, so this is one flat list over whatever they let
 * through.
 *
 * The list fills the height beside the chart and scrolls inside it, so the
 * column it sits in decides how much shows rather than the number of members.
 */
export function MembersList(props: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState(FALLBACK_LIST_HEIGHT);

  // The window needs its height as a number, so the space left beside the
  // chart has to be measured rather than handed over in CSS.
  useEffect(() => {
    const element = listRef.current;
    if (element === null) return undefined;
    const observer = new ResizeObserver((entries) => {
      setListHeight(entries[0].contentRect.height);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Search spans serial and location, so a unit is found without knowing where
  // it stood.
  const activeMembers = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (term.length === 0) return props.members;
    return props.members.filter(
      (member) =>
        member.serialNumber.toLowerCase().includes(term) ||
        member.locationName.toLowerCase().includes(term)
    );
  }, [props.members, searchQuery]);

  return (
    <Stack gap={2} sx={{ height: '100%', minHeight: 0 }}>
      {props.onClose !== undefined && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">Members</Typography>
          <IconButton
            size="small"
            aria-label="Hide assets"
            onClick={props.onClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      )}
      <TextField
        fullWidth
        size="small"
        placeholder="Search serial or location"
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
        }}
      />

      {/* The measured box stays mounted through an empty day, so the observer
          attached to it survives a search that matches nothing. */}
      <Box ref={listRef} sx={{ flex: 1, minHeight: 0 }}>
        {activeMembers.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim().length > 0
                ? 'No assets match that search'
                : props.emptyMessage}
            </Typography>
          </Paper>
        ) : (
          // A project can hold thousands of members, so only the visible rows
          // are mounted. The row height is fixed to keep the window exact.
          <FixedSizeList
            height={listHeight}
            width="100%"
            itemSize={ROW_HEIGHT}
            itemCount={activeMembers.length}
            itemData={activeMembers}
            overscanCount={8}
          >
            {MemberRow}
          </FixedSizeList>
        )}
      </Box>
    </Stack>
  );
}

// React-window takes this as a component type, so an inline arrow would give
// every row a new type on each render and remount the whole visible window.
function MemberRow(props: ListChildComponentProps<MemberOnDay[]>) {
  const member = props.data[props.index];
  return (
    <Box style={props.style} sx={{ pb: 1, pr: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          borderLeft: 3,
          borderRadius: '5px',
          borderColor: 'success.main',
          pl: 1,
          py: 0.5,
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Link
            to="/assets/$assetKey"
            params={{ assetKey: member.key }}
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <Typography variant="body2" component="span">
              {member.serialNumber}
            </Typography>
            <OpenInNew sx={{ fontSize: 14 }} />
          </Link>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {member.locationName}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
